import { EventSnapshot, Market, Outcome } from '../api/types';
import {
  find1x2Market,
  find1stHalfMarket,
  findRestzeitGeneric,
  find1stHalfRestzeitGeneric,
  findOUGeneric,
  findNextGoal,
  findHandicap,
  findBTTSGeneric,
  findDCGeneric,
  findH2H,
  findSatzSieger,
  findOddEven,
  findTeamTotal,
  findOvertimeYesNo,
  find2ndHalfHandicap,
  find2ndHalfTotal,
  findSatzHandicap,
  findSatzTotal,
  findQuarter1x2,
  findQuarterTotal,
  findPointsTotal,
  findOUGenericMulti
} from './marketParsers';
import { resolveOutcomeName } from './marketTranslations';

export interface MobileMarketRow {
  attrMarket: Market | undefined;
  items: { label: string; outcome: Outcome; market: Market; tipp: string }[];
}

export interface MobileMarketData {
  rows: MobileMarketRow[];
  skeletonCount: number;
}

export function getMobileMarketData(layoutGrp: string, ev: EventSnapshot, locale: string = 'de'): MobileMarketData | null {
  let attrMarket: Market | undefined;
  let items: { label: string; outcome: Outcome; market: Market; tipp: string }[] = [];
  let rows: MobileMarketRow[] = [];
  let skeletonCount = 0;

  switch (layoutGrp) {
    case 'Tipp': {
      const m = find1x2Market(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Heimsieg' },
          { label: 'X', outcome: m.draw, market: m.market, tipp: 'Unentschieden' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Auswärtssieg' },
        ];
      }
      break;
    }
    case '1. Halbzeit': {
      const m = find1stHalfMarket(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: '1. Halbzeit Heim' },
          { label: 'X', outcome: m.draw, market: m.market, tipp: '1. Halbzeit Unentschieden' },
          { label: '2', outcome: m.away, market: m.market, tipp: '1. Halbzeit Auswärts' },
        ];
      }
      break;
    }
    case 'Tipp Restzeit': {
      const m = findRestzeitGeneric(ev.markets);
      attrMarket = undefined;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Restzeit Heimsieg' },
          { label: 'X', outcome: m.draw, market: m.market, tipp: 'Restzeit Unentschieden' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Restzeit Auswärtssieg' },
        ];
      }
      break;
    }
    case '1.Hz Tipp Restzeit': {
      const m = find1stHalfRestzeitGeneric(ev.markets);
      attrMarket = undefined;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: '1.Hz Restzeit Heimsieg' },
          { label: 'X', outcome: m.draw, market: m.market, tipp: '1.Hz Restzeit Unentschieden' },
          { label: '2', outcome: m.away, market: m.market, tipp: '1.Hz Restzeit Auswärtssieg' },
        ];
      }
      break;
    }
    case 'Über/Unter': {
      if (ev.event.sport_name === 'Soccer' || ev.event.sport_id === 'sr:sport:1') {
        const ms = findOUGenericMulti(ev.markets, false, ev, 3);
        skeletonCount = 2;
        if (ms.length > 0) {
          rows = ms.map(m => ({
            attrMarket: m.market,
            items: [
              { label: 'Über', outcome: m.over, market: m.market, tipp: 'Über' },
              { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Unter' }
            ]
          }));
        }
      } else {
        const m = findOUGeneric(ev.markets, false, ev);
        attrMarket = m?.market;
        skeletonCount = 2;
        if (m) {
          items = [
            { label: 'Über', outcome: m.over, market: m.market, tipp: 'Über' },
            { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Unter' },
          ];
        }
      }
      break;
    }
    case 'Halbzeit-Über/Unter': {
      if (ev.event.sport_name === 'Soccer' || ev.event.sport_id === 'sr:sport:1') {
        const ms = findOUGenericMulti(ev.markets, true, ev, 3);
        skeletonCount = 2;
        if (ms.length > 0) {
          rows = ms.map(m => ({
            attrMarket: m.market,
            items: [
              { label: 'Über', outcome: m.over, market: m.market, tipp: '1. Halbzeit Über' },
              { label: 'Unter', outcome: m.under, market: m.market, tipp: '1. Halbzeit Unter' }
            ]
          }));
        }
      } else {
        const m = findOUGeneric(ev.markets, true, ev);
        attrMarket = m?.market;
        skeletonCount = 2;
        if (m) {
          items = [
            { label: 'Über', outcome: m.over, market: m.market, tipp: '1. Halbzeit Über' },
            { label: 'Unter', outcome: m.under, market: m.market, tipp: '1. Halbzeit Unter' },
          ];
        }
      }
      break;
    }
    case 'Nächstes Tor': {
      const m = findNextGoal(ev.markets);
      attrMarket = undefined;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Nächstes Tor Heim' },
          { label: 'Kein Tor', outcome: m.none, market: m.market, tipp: 'Kein Tor' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Nächstes Tor Auswärts' },
        ];
      }
      break;
    }
    case 'Handicap':
    case 'Satz-Handicap': {
      const m = layoutGrp === 'Handicap' ? findHandicap(ev.markets) : findSatzHandicap(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        const buttons = [
          { label: '1', outcome: m.o1, market: m.market, tipp: 'Handicap Heim' },
        ];
        if (m.o3) buttons.push({ label: 'X', outcome: m.o3, market: m.market, tipp: 'Handicap Unentschieden' });
        buttons.push({ label: '2', outcome: m.o2, market: m.market, tipp: 'Handicap Auswärts' });
        items = buttons;
        skeletonCount = buttons.length;
      }
      break;
    }
    case '1.Hz Handicap': {
      const m = findHandicap(ev.markets, true);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        const buttons = [{ label: '1', outcome: m.o1, market: m.market, tipp: '1.Hz Handicap Heim' }];
        if (m.o3) buttons.push({ label: 'X', outcome: m.o3, market: m.market, tipp: '1.Hz Handicap Unentschieden' });
        buttons.push({ label: '2', outcome: m.o2, market: m.market, tipp: '1.Hz Handicap Auswärts' });
        items = buttons;
        skeletonCount = buttons.length;
      }
      break;
    }
    case '2. Halbzeit Handicap': {
      const m = find2ndHalfHandicap(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        const buttons = [{ label: '1', outcome: m.o1, market: m.market, tipp: '2.Hz Handicap Heim' }];
        if (m.o3) buttons.push({ label: 'X', outcome: m.o3, market: m.market, tipp: '2.Hz Handicap Unentschieden' });
        buttons.push({ label: '2', outcome: m.o2, market: m.market, tipp: '2.Hz Handicap Auswärts' });
        items = buttons;
        skeletonCount = buttons.length;
      }
      break;
    }
    case 'Beide Teams treffen': {
      const m = findBTTSGeneric(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Ja' },
          { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Nein' },
        ];
      }
      break;
    }
    case 'Doppelte Chance': {
      const m = findDCGeneric(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1X', outcome: m.o1x, market: m.market, tipp: '1X' },
          { label: '12', outcome: m.o12, market: m.market, tipp: '12' },
          { label: 'X2', outcome: m.ox2, market: m.market, tipp: 'X2' },
        ];
      }
      break;
    }
    case '1.Hz Doppelte Chance': {
      const m = findDCGeneric(ev.markets, true);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1X', outcome: m.o1x, market: m.market, tipp: '1.Hz 1X' },
          { label: '12', outcome: m.o12, market: m.market, tipp: '1.Hz 12' },
          { label: 'X2', outcome: m.ox2, market: m.market, tipp: '1.Hz X2' },
        ];
      }
      break;
    }
    case 'Head-To-Head':
    case 'Sieger':
    case 'Draw No Bet': {
      const m = findH2H(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Sieger Heim' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Sieger Auswärts' },
        ];
      }
      break;
    }
    case 'Satz Sieger': {
      const m = findSatzSieger(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Satz Sieger Heim' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Satz Sieger Auswärts' },
        ];
      }
      break;
    }
    case 'Gerade/Ungerade': {
      const m = findOddEven(ev.markets, false);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        const eventName = `${ev.event.home.name} - ${ev.event.away.name}`;
        const evenLabel = resolveOutcomeName(m.market.market_id, m.even.outcome_id, locale, m.market.specifiers, eventName, m.even.name) || m.even.name;
        const oddLabel = resolveOutcomeName(m.market.market_id, m.odd.outcome_id, locale, m.market.specifiers, eventName, m.odd.name) || m.odd.name;
        const evenStr = evenLabel.charAt(0).toUpperCase() + evenLabel.slice(1);
        const oddStr = oddLabel.charAt(0).toUpperCase() + oddLabel.slice(1);
        items = [
          { label: evenStr, outcome: m.even, market: m.market, tipp: evenStr },
          { label: oddStr, outcome: m.odd, market: m.market, tipp: oddStr },
        ];
      }
      break;
    }
    case '1.Hz Gerade/Ungerade': {
      const m = findOddEven(ev.markets, true);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        const eventName = `${ev.event.home.name} - ${ev.event.away.name}`;
        const evenLabel = resolveOutcomeName(m.market.market_id, m.even.outcome_id, locale, m.market.specifiers, eventName, m.even.name) || m.even.name;
        const oddLabel = resolveOutcomeName(m.market.market_id, m.odd.outcome_id, locale, m.market.specifiers, eventName, m.odd.name) || m.odd.name;
        const evenStr = evenLabel.charAt(0).toUpperCase() + evenLabel.slice(1);
        const oddStr = oddLabel.charAt(0).toUpperCase() + oddLabel.slice(1);
        items = [
          { label: evenStr, outcome: m.even, market: m.market, tipp: `1.Hz ${evenStr}` },
          { label: oddStr, outcome: m.odd, market: m.market, tipp: `1.Hz ${oddStr}` },
        ];
      }
      break;
    }
    case 'Heim Über/Unter': {
      const m = findTeamTotal(ev.markets, true);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Heim Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Heim Unter' },
        ];
      }
      break;
    }
    case 'Auswärts Über/Unter': {
      const m = findTeamTotal(ev.markets, false);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Auswärts Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Auswärts Unter' },
        ];
      }
      break;
    }
    case '1.Hz Heim Über/Unter': {
      const m = findTeamTotal(ev.markets, true, true);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: '1.Hz Heim Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: '1.Hz Heim Unter' },
        ];
      }
      break;
    }
    case '1.Hz Auswärts Über/Unter': {
      const m = findTeamTotal(ev.markets, false, true);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: '1.Hz Auswärts Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: '1.Hz Auswärts Unter' },
        ];
      }
      break;
    }
    case '2. Halbzeit Über/Unter': {
      const m = find2ndHalfTotal(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: '2.Hz Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: '2.Hz Unter' },
        ];
      }
      break;
    }
    case 'Verlängerung Ja/Nein': {
      const m = findOvertimeYesNo(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Ja', outcome: m.yes, market: m.market, tipp: 'Verl. Ja' },
          { label: 'Nein', outcome: m.no, market: m.market, tipp: 'Verl. Nein' },
        ];
      }
      break;
    }
    case 'Viertel 1x2': {
      const m = findQuarter1x2(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 3;
      if (m) {
        items = [
          { label: '1', outcome: m.home, market: m.market, tipp: 'Viertel Heim' },
          { label: 'X', outcome: m.draw, market: m.market, tipp: 'Viertel Unentschieden' },
          { label: '2', outcome: m.away, market: m.market, tipp: 'Viertel Auswärts' },
        ];
      }
      break;
    }
    case 'Viertel Über/Unter': {
      const m = findQuarterTotal(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Viertel Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Viertel Unter' },
        ];
      }
      break;
    }
    case 'Satz Über/Unter': {
      const m = findSatzTotal(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Satz Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Satz Unter' },
        ];
      }
      break;
    }
    case 'Toplam Sayı Alt/Üst': {
      const m = findPointsTotal(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Punkte Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Punkte Unter' },
        ];
      }
      break;
    }
    case 'Set Toplam Sayı Alt/Üst': {
      const m = findSatzTotal(ev.markets);
      attrMarket = m?.market;
      skeletonCount = 2;
      if (m) {
        items = [
          { label: 'Über', outcome: m.over, market: m.market, tipp: 'Satz Punkte Über' },
          { label: 'Unter', outcome: m.under, market: m.market, tipp: 'Satz Punkte Unter' },
        ];
      }
      break;
    }
    default:
      return null;
  }

  if (rows.length === 0) {
    if (!attrMarket && items.length === 0 && skeletonCount === 0) return null;
    rows = [{ attrMarket, items }];
  }
  
  return {
    rows,
    skeletonCount
  };
}
